import React, { useState, useEffect } from "react";
import { FaLock, FaEye, FaEyeSlash, FaShieldAlt, FaKey } from "react-icons/fa";
import { useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createPasswordAction } from "../redux/actions/createPasswordAction";
import Loading from "./Loading";

function CreatePassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const store = useSelector((state) => state.createPassword);
  const { loading, success, error } = store;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [state, setState] = useState({
    password: "",
    confirmpassword: "",
  });

  useEffect(() => {
    if (success) {
      navigate("/predashboard");
    }
  }, [success, navigate]);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    setState({
      ...state,
      [name]: value,
    });
  };

  const submitHandler = (e) => {
    e.preventDefault();

    if (!state.password || !state.confirmpassword) {
      return;
    }
    let fileNumber = JSON.parse(localStorage.getItem("file_number"));
    let phoneNumber = JSON.parse(localStorage.getItem("phone"));
    console.log('fileNumber', fileNumber);
    dispatch(createPasswordAction(phoneNumber, fileNumber, state.password, state.confirmpassword));

    setState({
      password: "",
      confirmpassword: "",
    });
  };

  const passwordRequirements = [
    { text: "At least 6 characters", met: state.password.length >= 6 },
    { text: "Contains uppercase letter", met: /[A-Z]/.test(state.password) },
    { text: "Contains lowercase letter", met: /[a-z]/.test(state.password) },
    { text: "Contains number", met: /\d/.test(state.password) },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary100 via-blue-500 to-purple-600 flex flex-col justify-center items-center p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header Section */}
        <div className="bg-gradient-to-r from-primary100 to-blue-600 px-8 py-12 text-center">
          <div className="w-20 h-20 bg-white rounded-full flex justify-center items-center mx-auto mb-4 shadow-lg">
            <FaLock className="text-primary100 text-2xl" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Create Password</h1>
          <p className="text-blue-100 text-sm">
            Set up a secure password for your account
          </p>
        </div>

        {/* Content Section */}
        <div className="px-8 py-8">
          {/* Security Features */}
          <div className="flex justify-center space-x-6 mb-8">
            <div className="flex items-center space-x-2 text-green-600">
              <FaShieldAlt className="text-sm" />
              <span className="text-xs font-medium">Secure</span>
            </div>
            <div className="flex items-center space-x-2 text-blue-600">
              <FaKey className="text-sm" />
              <span className="text-xs font-medium">Encrypted</span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
              <p className="text-red-600 text-sm">{error?.message || error}</p>
            </div>
          )}

          <form onSubmit={submitHandler} className="space-y-6">
            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Create Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={state.password}
                  onChange={changeHandler}
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary100 focus:border-transparent transition-all duration-200 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Confirm Password Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmpassword"
                  value={state.confirmpassword}
                  onChange={changeHandler}
                  placeholder="Confirm your password"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary100 focus:border-transparent transition-all duration-200 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            {state.password && (
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Password Requirements:</h4>
                <div className="space-y-2">
                  {passwordRequirements.map((req, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${req.met ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <span className={`text-xs ${req.met ? 'text-green-600' : 'text-gray-500'}`}>
                        {req.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              {loading ? (
                <div className="flex justify-center py-4">
                  <Loading />
                </div>
              ) : (
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-primary100 to-blue-600 text-white py-4 rounded-lg font-semibold text-lg hover:from-blue-600 hover:to-primary100 transform hover:scale-[1.02] transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  Create Password
                </button>
              )}
            </div>
          </form>

          {/* Help Text */}
          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Your password will be encrypted and stored securely
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreatePassword;
